/**
 * An HTTP failure that still knows which status produced it.
 *
 * The queue host page decides whether to renew its token on `error.status === 401`.
 * An error that carries only a message leaves that recovery path unreachable: the
 * host cannot tell an expired session from any other failure.
 *
 * Unlike the RevenueQueue `ApiError`, the message is kept exactly as given. Hosts
 * that are already in production read the CareQueue message text
 * ("Authentication failed - invalid or expired token"), so it must not change.
 */
export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/** Message of the error thrown for an HTTP 401 response. Hosts read this text. */
export const UNAUTHORIZED_MESSAGE = 'Authentication failed - invalid or expired token';

/** Message of the error thrown for an HTTP 403 response. Hosts read this text. */
export const FORBIDDEN_MESSAGE = 'Authorization failed - insufficient permissions';

/** Read an HTTP status off an unknown thrown value, if it carries one. */
export function getErrorStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * Whether a thrown value represents an expired or rejected credential.
 *
 * Deliberately 401 only. A 403 means the credential was understood and refused,
 * so re-authenticating would not help and telling the host the session expired
 * would be wrong.
 */
export function isUnauthorized(error: unknown): boolean {
  return getErrorStatus(error) === 401;
}

/** Set on the value a `fetch` call rejected with (see fetchMarkingRejection). */
const FETCH_REJECTION = Symbol('fetchRejection');

/**
 * Mark a value that `fetch` rejected with, so isTransientError can tell a network failure
 * from any other error without an HTTP status (a parse error, a code bug, a failed
 * tokenProvider). The value itself is kept, so its type, name and message do not change.
 */
export function markFetchRejection<T>(error: T): T {
  if (
    typeof error === 'object' &&
    error !== null &&
    Object.isExtensible(error) &&
    !isFetchRejection(error)
  ) {
    Object.defineProperty(error, FETCH_REJECTION, { value: true });
  }
  return error;
}

/** Whether a thrown value is one that `fetch` rejected with (marked by markFetchRejection). */
export function isFetchRejection(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { [FETCH_REJECTION]?: unknown })[FETCH_REJECTION] === true
  );
}

/** `fetch`, with a rejection marked by markFetchRejection before it is rethrown. */
export async function fetchMarkingRejection(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (error) {
    throw markFetchRejection(error);
  }
}

/**
 * Whether a failed request can succeed if it is simply sent again later.
 *
 * True for a 5xx from the WorkQueue API (a 503 includes the unknown-kid answer that
 * hosts treat as transient) and for a network failure: a rejection of the `fetch` call
 * itself (fetchMarkingRejection), not an abort.
 * False for every 4xx: a 401 needs a new token from the host and a 403 is a refusal,
 * so repeating the request would only repeat the answer. False for every other error
 * without a status (a response body that is not JSON, a code bug, a failed
 * tokenProvider): repeating the request does not change those.
 */
export function isTransientError(error: unknown): boolean {
  const status = getErrorStatus(error);
  if (status === undefined) {
    return isFetchRejection(error) && (error as { name?: unknown }).name !== 'AbortError';
  }
  return status >= 500;
}
